import { useState, useEffect } from 'react'
import axios from '@fs/zion-axios'
import { setPreference } from '@fs/zion-preferences'
import { initialMemoryBookState } from '../../providers/MemoryBooksReducer'

const endpointURL = '/service/memory-books'

// This is a helper function to make API calls a bit more robust and keep code DRY
// My original intention was to have this handle the error and success
// Messages, but they are tricky because they are React hooks
// This still gives you access to onStart and onComplete for loading,
// OnSuccess for succeeding, and onError for failing
// It may be useful to turn thi into a custom hook but I did not have enough
// time to figure that out

// This is more or less an experiment, if you find it complicates the code
// Too much, feel free to remove it
export async function apiRequest({
  apiFunction,
  onStart = () => {},
  onSuccess = () => {},
  onError = () => {},
  onComplete = () => {},
}) {
  let status = null
  try {
    onStart()
    const response = await apiFunction()
    status = response?.status || null
    onSuccess(response)
    return { status, error: null, response }
  } catch (err) {
    console.error(err)
    onError(err)
    return { status: null, error: err, response: null }
  } finally {
    onComplete()
  }
}

// POST /books/{bookID}/pages
// Creates 1 or many blank pages in a book at the specified page number
// - bookID: The ID of the book to add pages to
// - startingPageNumber: The page number to start adding pages at
// - pageCount: The number of pages to add
export const postBlankBookPages = async ({
  bookId,
  startingPageNumber,
  pageCount,
  onSuccess,
  onComplete,
  onError,
  onStart,
}) => {
  // We are adding 1 to the index because the UI wants the page added after
  // the page passed in, not at the index of the page passed in
  startingPageNumber = Number(startingPageNumber)
  startingPageNumber += 1
  const { status, error, response } = await apiRequest({
    apiFunction: async () => {
      return axios.post(`${endpointURL}/books/${bookId}/pages`, {
        startingPageNumber,
        pageCount,
      })
    },
    onStart,
    onSuccess,
    onError,
    onComplete,
  })
  return { status, error, response }
}

// DELETE /books/{bookID}/pages/{pageNumber}
// Deletes a page from a book
// - bookID: The ID of the book to delete the page from
// - pageNumber: The page number to delete
export const deletePageAPI = async ({ bookId, pageNumber, onSuccess, onComplete, onError, onStart }) => {
  const { status, error, response } = await apiRequest({
    apiFunction: async () => {
      return axios.delete(`${endpointURL}/books/${bookId}/pages/${pageNumber}`)
    },
    onStart,
    onSuccess,
    onError,
    onComplete,
  })

  return { status, error, response }
}

export const updatePageLayoutAPI = async ({
  bookId,
  templateId,
  page,
  elements,
  stickers,
  customLayoutElements,
  backgroundId,
  onSuccess,
  onComplete,
  onError,
  onStart,
}) => {
  const requestPayload = {
    templateId,
    elements,
    page,
  }

  if (stickers !== undefined) {
    requestPayload.stickers = stickers
  }

  if (customLayoutElements !== undefined) {
    requestPayload.customLayoutElements = customLayoutElements
  }

  if (backgroundId !== undefined) {
    requestPayload.backgroundId = backgroundId
  }

  const { status, error, response } = await apiRequest({
    apiFunction: async () => {
      return axios.put(`${endpointURL}/books/${bookId}/pages/${page}`, requestPayload)
    },
    onStart,
    onSuccess,
    onError,
    onComplete,
  })

  return { status, error, response }
}

export const updatePageOrderAPI = async ({ bookId, page, newPageNumber, onSuccess }) => {
  const { status, error, response } = await apiRequest({
    apiFunction: async () => {
      return axios.put(`${endpointURL}/books/${bookId}/pages/${page}/renumber/${newPageNumber}`)
    },

    onSuccess,
  })

  return { status, error, response }
}

// GET /books
// Gets all books without their actual details, but makes it so we can get the
// bookID to get the details of a single book
export const useGetAllMemoryBooks = () => {
  const [books, setBooks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let isMounted = true

    const fetchBooks = async () => {
      try {
        const allBooks = await axios.get(`${endpointURL}/books`)
        if (isMounted) {
          setBooks([...allBooks.data])
        }
      } catch (err) {
        if (isMounted) {
          setError(err)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchBooks()

    return () => {
      isMounted = false
    }
  }, [])

  return { books, loading, error }
}

// GET /books/{bookID}
// Gets the first book in the users list of books
export const useGetFirstBook = (dispatch, isAuthenticated = true) => {
  const [firstBook, setFirstBookData] = useState({})
  const [loading, setLoading] = useState(isAuthenticated)
  const [error, setError] = useState(null)
  const [bookId, setBookId] = useState(null)

  useEffect(() => {
    let isMounted = true

    const fetchFirstBook = async () => {
      try {
        const initialBook = initialMemoryBookState() // Move inside effect to avoid dependency issues
        const allBooks = await axios.get(`${endpointURL}/books`)
        let postBookResponse = null

        if (allBooks.data.length === 0) {
          postBookResponse = await postNewMemoryBook(initialBook) // Await the POST request
        }

        const firstBookDetails = postBookResponse ? postBookResponse.data : allBooks.data[0]
        const firstPagesResponse = await axios.get(`${endpointURL}/books/${firstBookDetails.id}/pages`)

        if (isMounted) {
          const bookData = {
            contentPages: firstPagesResponse.data,
            currentPages: ['back', 'front'], // Remove dependency on isTabletOrSmaller
            ...firstBookDetails,
          }
          dispatch({ type: 'SET_BOOK', payload: bookData })
          setBookId(firstBookDetails.id)
          setFirstBookData(bookData)

          // Set the preference for the first book
          await setPreference('chinese.memoryBooks.hasMemoryBook', { id: firstBookDetails.id, exists: true })
        }
      } catch (err) {
        if (isMounted) {
          setError(err)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    // Don't fetch if user is not authenticated
    if (!isAuthenticated) {
      setLoading(false)
      return () => {
        isMounted = false
      }
    }

    // Reset state when starting to fetch
    setLoading(true)
    setError(null)
    fetchFirstBook()

    // Cleanup function to cancel pending requests if component unmounts
    return () => {
      isMounted = false
    }
  }, [dispatch, isAuthenticated]) // Include isAuthenticated as dependency

  return { firstBook, bookId, loading, error }
}

// GET /books/{bookID}
// Gets book by ID in the users list of books
// Returns null for bookData if no books exist or if the specified book is not found
export async function getBookById(id) {
  try {
    const allBooks = await axios.get(`${endpointURL}/books`)

    // If no books exist, return null
    if (allBooks.data.length === 0) {
      return { bookData: null, error: null }
    }

    // Find the specific book
    const bookDetails = allBooks.data.find((book) => book.id === id)
    if (!bookDetails) {
      return { bookData: null, error: null }
    }

    // Get the book's pages
    const bookPages = await axios.get(`${endpointURL}/books/${id}/pages`)

    const bookData = {
      contentPages: bookPages.data,
      currentPages: ['back', 'front'],
      ...bookDetails,
    }

    // Set preference
    await setPreference('chinese.memoryBooks.hasMemoryBook', { id: bookDetails.id, exists: true })

    return { bookData, error: null }
  } catch (error) {
    console.error('Failed to fetch book by ID:', error)
    return { bookData: null, error }
  }
}

/// //////////////Base API Calls///////////////////////

// POST /books
// - Create a new Book, you need to pass in JSON for a single book NOT multiple
//   like the /books would imply
// - This is also NOT a PUT, you can't update here new books only
export async function postNewMemoryBook(books) {
  return axios.post(`${endpointURL}/books`, books)
}

export async function getAllBooks() {
  return axios.get(`${endpointURL}/books`)
}

// DELETE /books/{bookID}
// Deletes a book by ID
// - bookId: The ID of the book to delete
export const deleteMemoryBook = async ({ id, onSuccess, onComplete, onError, onStart }) => {
  const { status, error, response } = await apiRequest({
    apiFunction: async () => {
      return axios.delete(`${endpointURL}/books/${id}`)
    },
    onStart,
    onSuccess,
    onError,
    onComplete,
  })

  return { status, error, response }
}

// PUT /books/{bookID}
// Updates the Title of a memory book
// - bookId: The ID of the book to delete
// - newTitle: The new title of the book
export const updateMemoryBookTitle = async ({ id, newTitle, onSuccess, onComplete, onError, onStart }) => {
  const { status, error, response } = await apiRequest({
    apiFunction: async () => {
      return axios.put(`${endpointURL}/books/${id}`, { title: newTitle })
    },
    onStart,
    onSuccess,
    onError,
    onComplete,
  })

  return { status, error, response }
}
